import { useEffect, useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { DatePicker, Select, Space, Switch, notification } from 'antd';
import { debounce } from 'lodash';
import type { StudioLinkRelation, StudioSummary } from '../contract';
import {
  STUDIO_PAGE_LIMIT,
  StudioViewer,
  getStudioLog,
  getStudioMembers,
  studioAbilities,
  useStudioLoad,
} from '../api';
import { mapStudioError } from '../studio-error';
import { LINK_RELATION_OPTIONS, LOG_ACTION_OPTIONS } from '../labels';
import { displayName } from '../format';
import LogTable from '../components/log-table';
import { StudioLoadError } from './studio-header';
import styles from '../styles.module.scss';

export interface LogTabProps {
  studioId: number;
  studio: StudioSummary;
  viewer: StudioViewer;
}

type PersonOption = { value: number; label: string };

/**
 * Tab Lịch sử của Chi tiết Studio (SPEC Studio mục 8.7.9, 6.4): nhật ký S22 lọc theo khoảng ngày (ngày VN),
 * Việc, Người thao tác (từ các dòng đã tải + Quản lý, trợ lý), Người liên quan (tìm trong thành viên có tài
 * khoản), Liên quan; Admin / SMOD thêm "Chỉ dòng có cờ" và cột Cờ (Admin bấm cờ đang mở để khép cờ).
 */
const LogTab = ({ studioId, studio, viewer }: LogTabProps) => {
  const abilities = studioAbilities(viewer.mine, studioId);
  const seesFlags = abilities.seesFlags;

  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [actions, setActions] = useState<string[]>([]);
  const [actorId, setActorId] = useState<number | undefined>();
  const [subject, setSubject] = useState<PersonOption | undefined>();
  const [relation, setRelation] = useState<StudioLinkRelation | undefined>();
  const [flagged, setFlagged] = useState(false);
  const [page, setPage] = useState(1);

  const from = range ? range[0].format('YYYY-MM-DD') : undefined;
  const to = range ? range[1].format('YYYY-MM-DD') : undefined;

  const log = useStudioLoad(
    () =>
      getStudioLog(studioId, {
        from,
        to,
        actions,
        actorId,
        subjectId: subject?.value,
        relation,
        flagged: seesFlags && flagged,
        page,
        limit: STUDIO_PAGE_LIMIT,
      }),
    [studioId, from, to, actions, actorId, subject?.value, relation, seesFlags && flagged, page],
    { resetKey: studioId }
  );
  const logData = log.data;
  const rows = logData?.data || [];

  // Người thao tác: Quản lý + trợ lý của Studio, cộng mọi `actor` đã gặp trong các trang kết quả.
  const [seenActors, setSeenActors] = useState<Record<number, string>>({});
  useEffect(() => {
    const found: Record<number, string> = {};
    (logData?.data || []).forEach((r) => {
      if (r.actor?.id) found[r.actor.id] = r.actor.name;
    });
    if (!Object.keys(found).length) return;
    setSeenActors((prev) =>
      Object.keys(found).every((id) => prev[Number(id)] === found[Number(id)])
        ? prev
        : { ...prev, ...found }
    );
  }, [logData]);
  const actorOptions: PersonOption[] = useMemo(() => {
    const byId = new Map<number, string>();
    if (studio.manager) byId.set(studio.manager.id, displayName(studio.manager));
    (studio.assistants || []).forEach((a) => byId.set(a.id, displayName(a)));
    Object.keys(seenActors).forEach((id) => {
      if (!byId.has(Number(id))) byId.set(Number(id), seenActors[Number(id)]);
    });
    return Array.from(byId.entries()).map(([value, label]) => ({ value, label }));
  }, [studio.manager, studio.assistants, seenActors]);

  // Người liên quan: tìm trong thành viên có tài khoản (subjectId là id tài khoản).
  const [subjectOptions, setSubjectOptions] = useState<PersonOption[]>([]);
  const [searching, setSearching] = useState(false);
  const searchSubjects = useMemo(
    () =>
      debounce(async (text: string) => {
        const q = text.trim();
        if (!q) {
          setSubjectOptions([]);
          setSearching(false);
          return;
        }
        setSearching(true);
        try {
          const res = await getStudioMembers(studioId, { q, limit: 20 });
          setSubjectOptions(
            (res?.data || [])
              .filter((m) => m.userId !== null)
              .map((m) => ({
                value: m.userId as number,
                label: displayName({ nickName: m.nickName, username: m.username, uid: m.uid }),
              }))
          );
        } catch (err) {
          setSubjectOptions([]);
          notification.error({ message: mapStudioError(err) });
        } finally {
          setSearching(false);
        }
      }, 400),
    [studioId]
  );
  useEffect(() => () => searchSubjects.cancel(), [searchSubjects]);
  const subjectSelectOptions =
    subject && !subjectOptions.some((o) => o.value === subject.value)
      ? [subject, ...subjectOptions]
      : subjectOptions;

  const resetPage = () => setPage(1);

  return (
    <div className={styles.page}>
      <div className={styles.filters}>
        <DatePicker.RangePicker
          format="DD/MM/YYYY"
          value={range}
          onChange={(values) => {
            setRange(values && values[0] && values[1] ? [values[0], values[1]] : null);
            resetPage();
          }}
          placeholder={['Từ ngày', 'Đến ngày']}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Việc"
          value={actions}
          options={LOG_ACTION_OPTIONS}
          optionFilterProp="label"
          maxTagCount="responsive"
          onChange={(v: string[]) => {
            setActions(v);
            resetPage();
          }}
          style={{ minWidth: 220, maxWidth: 360 }}
        />
        <Select<number>
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Người thao tác"
          value={actorId}
          options={actorOptions}
          onChange={(v) => {
            setActorId(v);
            resetPage();
          }}
          style={{ width: 200 }}
        />
        <Select<number>
          allowClear
          showSearch
          filterOption={false}
          placeholder="Người liên quan"
          value={subject?.value}
          options={subjectSelectOptions}
          loading={searching}
          notFoundContent={searching ? 'Đang tìm…' : 'Gõ tên, TikTok ID hoặc UID để tìm'}
          onSearch={(text) => searchSubjects(text)}
          onChange={(v) => {
            setSubject(
              v === undefined || v === null
                ? undefined
                : subjectSelectOptions.find((o) => o.value === v) || { value: v, label: String(v) }
            );
            resetPage();
          }}
          style={{ width: 220 }}
        />
        <Select<StudioLinkRelation>
          allowClear
          placeholder="Liên quan"
          value={relation}
          options={LINK_RELATION_OPTIONS}
          onChange={(v) => {
            setRelation(v);
            resetPage();
          }}
          style={{ width: 160 }}
        />
        {seesFlags && (
          <Space size={6}>
            <Switch
              checked={flagged}
              onChange={(checked) => {
                setFlagged(checked);
                resetPage();
              }}
              aria-label="Chỉ dòng có cờ"
            />
            <span>Chỉ dòng có cờ</span>
          </Space>
        )}
      </div>

      {log.error && <StudioLoadError error={log.error} onRetry={log.reload} />}

      <LogTable
        rows={rows}
        loading={log.loading}
        showFlags={seesFlags}
        canCloseFlag={abilities.admin}
        onFlagClosed={log.reload}
        onDisputed={log.reload}
        pagination={
          log.data
            ? {
                page,
                pageSize: STUDIO_PAGE_LIMIT,
                total: log.data.metadata.total,
                onChange: setPage,
              }
            : undefined
        }
      />
    </div>
  );
};

export default LogTab;
